import Router from 'next/router';
import { useCallback } from 'react';

import { type WorkspaceAgentAssetPill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import {
  type FlattenedWorkspaceAgentAssetPillItem,
  flattenWorkspaceAgentAssetPillItems,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/flattenWorkspaceAgentAssetPillItems';
import { QueryParamNames } from '~/constants/search';
import { useGoToPrivateAssetPage } from '~/hooks/useGoToPrivateAssetPage';
import { getClipIdFromPath } from '~/utils/PathUtils';

/**
 * Opens the clip an asset pill cites in the asset modal. Reuses the same navigation path as other
 * in-app asset opens (`/c/<clipId>`). Callers that want Info must pass `amTab: null` — clip changes
 * otherwise keep the current right-panel tab.
 */
export const useOpenWorkspaceAgentAssetPill = () => {
  const { goToAssetPage } = useGoToPrivateAssetPage();

  const openAssetClip = useCallback(
    (clipId: string) => {
      const isClipRouteActive = Boolean(getClipIdFromPath(Router.asPath));

      goToAssetPage({
        asset: { id: clipId },
        isAssetModalAlreadyOpen: isClipRouteActive,
        query: {
          [QueryParamNames.amTab]: null,
          [QueryParamNames.discussionId]: null,
        },
        trackLocation: 'card-click',
      });
    },
    [goToAssetPage],
  );

  const openAssetItem = useCallback(
    (item: FlattenedWorkspaceAgentAssetPillItem) => {
      if (!item.clipId) {
        return;
      }

      openAssetClip(item.clipId);
    },
    [openAssetClip],
  );

  const openAssetPill = useCallback(
    (pill: WorkspaceAgentAssetPill) => {
      const item = flattenWorkspaceAgentAssetPillItems([pill])[0];

      if (item) {
        openAssetItem(item);
      }
    },
    [openAssetItem],
  );

  return { openAssetClip, openAssetItem, openAssetPill };
};
